/**
 * scoring.js
 *
 * Pure scoring functions over the instrument JSON. No React, no DOM —
 * easy to unit-test and to reuse server-side.
 */

// Legacy: prior threshold for the now-retired `borderline` flag. Kept for
// any external imports that still reference it; not used internally.
const BORDERLINE_THRESHOLD = 0.1;

// Default Indeterminate threshold: any candidate whose curiosity OR mastery
// score is within ±DEFAULT_INDETERMINATE_THRESHOLD of zero lands in
// instrument.indeterminate instead of one of the four quadrants. Instruments
// can override via instrument.indeterminateThreshold. The 0.30 default was
// chosen against the random-clicking Monte Carlo (axis std ≈ 0.19, so ±0.30
// flags ~70% of pure-random candidates as Mixed Profile).
const DEFAULT_INDETERMINATE_THRESHOLD = 0.30;

/**
 * Compute normalized axis scores for a candidate.
 *
 * @param {object} instrument - parsed instrument JSON.
 * @param {Record<string, number>} responses - map of questionId -> selected option index.
 * @returns {Record<string, number>} map of axisId -> normalized score in [-1, 1].
 */
export function computeAxisScores(instrument, responses) {
  const axisRaw = {};
  const axisMax = {};

  for (const axis of instrument.axes) {
    axisRaw[axis.id] = 0;
    axisMax[axis.id] = 0;
  }

  // Allowlist the disposition types rather than denylisting technical-mcq.
  // Only likert-4 / forced-choice items carry {axis, score} options and feed
  // the axis scores. Anything else (technical-mcq, open-response, future
  // knowledge/qualitative types) is explicitly skipped — never folded in by
  // accident. See the construct-principle note in CLAUDE.md.
  const DISPOSITION_TYPES = new Set(['likert-4', 'forced-choice']);

  for (const group of instrument.groups) {
    for (const q of group.questions) {
      if (!DISPOSITION_TYPES.has(q.type)) continue;
      const weight = q.weight ?? 1;
      // For max-possible contribution, take the option with the largest |score|.
      const optMax = Math.max(...q.options.map((o) => Math.abs(o.score)));
      const axisForQ = q.options[0]?.axis;
      if (axisForQ && axisMax[axisForQ] !== undefined) {
        axisMax[axisForQ] += optMax * weight;
      }
      const choiceIdx = responses[q.id];
      if (choiceIdx == null) continue;
      const opt = q.options[choiceIdx];
      if (!opt) continue;
      if (axisRaw[opt.axis] === undefined) continue;
      axisRaw[opt.axis] += opt.score * weight;
    }
  }

  const normalized = {};
  for (const axis of instrument.axes) {
    const max = axisMax[axis.id] || 1;
    normalized[axis.id] = axisRaw[axis.id] / max;
  }
  return normalized;
}

/**
 * Compute the technical reasoning score for a candidate.
 *
 * Independent of axis/quadrant math: technical-mcq items are a knowledge
 * proficiency score, not a disposition typology. Surfaced alongside the
 * quadrant so a reviewer can weigh disposition and knowledge separately.
 *
 * @param {object} instrument - parsed instrument JSON.
 * @param {Record<string, number>} responses - map of questionId -> selected option index.
 * @returns {{ correct: number, total: number, percent: number, items: Array<{ questionId: string, correct: boolean }> }}
 *   `percent` is rounded to the nearest integer (0–100). `items[]` preserves
 *   question order. `total` of 0 yields `percent: 0` (no items, nothing to score).
 */
export function computeTechnicalScore(instrument, responses) {
  const items = [];
  let correct = 0;

  for (const group of instrument.groups) {
    for (const q of group.questions) {
      if (q.type !== 'technical-mcq') continue;
      const choiceIdx = responses?.[q.id];
      const opt = choiceIdx != null ? q.options[choiceIdx] : null;
      const isCorrect = opt?.correct === true;
      if (isCorrect) correct++;
      items.push({ questionId: q.id, correct: isCorrect });
    }
  }

  const total = items.length;
  const percent = total === 0 ? 0 : Math.round((correct / total) * 100);
  return { correct, total, percent, items };
}

/**
 * Collect the candidate's free-text answers to `open-response` items.
 *
 * Open-response items are a third, qualitative signal — reviewer-read, never
 * auto-scored. They feed neither `computeAxisScores` (typology) nor
 * `computeTechnicalScore` (proficiency). This helper just pairs each item's
 * stem with whatever the candidate typed, in instrument order, so the admin
 * view can render Q/A without re-deriving the question text.
 *
 * @param {object} instrument - parsed instrument JSON.
 * @param {Record<string, number|string>} responses - questionId -> answer.
 * @returns {Array<{ questionId: string, stem: string, answer: string }>}
 *   `answer` is '' when the candidate skipped the item.
 */
/**
 * Suffix under which a technical-mcq item's optional free-text rationale is
 * stored in the `responses` map, keyed as `<questionId>__rationale`. The
 * sibling-key scheme keeps the rationale string out of the question-id
 * namespace the scorers iterate, so `computeAxisScores` and
 * `computeTechnicalScore` (which read `responses[q.id]` as a choice index)
 * never see it. The rationale is reviewer-read, never scored — same construct
 * status as an open-response answer.
 */
export const RATIONALE_KEY_SUFFIX = '__rationale';

export function rationaleKey(questionId) {
  return `${questionId}${RATIONALE_KEY_SUFFIX}`;
}

/**
 * Collect the candidate's free-text rationales attached to `technical-mcq`
 * items that declare a `rationale` field. Pairs each item's stem with the
 * option the candidate chose and whatever they typed for "and why", in
 * instrument order, so the admin drill-down can render it without re-deriving
 * the question text or the option label.
 *
 * Like open-response, these rationales are reviewer-read and never scored —
 * the chosen option already feeds `computeTechnicalScore`; the "why" is a
 * parallel qualitative signal.
 *
 * @param {object} instrument - parsed instrument JSON.
 * @param {Record<string, number|string>} responses - questionId -> answer.
 * @returns {Array<{ questionId: string, stem: string, chosenLabel: string, answer: string }>}
 *   `chosenLabel` is '' when the MCQ was skipped; `answer` is '' when no
 *   rationale was typed.
 */
export function collectTechnicalRationales(instrument, responses) {
  const out = [];
  for (const group of instrument.groups) {
    for (const q of group.questions) {
      if (q.type !== 'technical-mcq' || !q.rationale) continue;
      const choiceIdx = responses?.[q.id];
      const chosen = typeof choiceIdx === 'number' ? q.options[choiceIdx] : null;
      const raw = responses?.[rationaleKey(q.id)];
      out.push({
        questionId: q.id,
        stem: q.stem || q.prompt || '',
        chosenLabel: chosen?.label || '',
        answer: typeof raw === 'string' ? raw : '',
      });
    }
  }
  return out;
}

export function collectOpenResponses(instrument, responses) {
  const out = [];
  for (const group of instrument.groups) {
    for (const q of group.questions) {
      if (q.type !== 'open-response') continue;
      const raw = responses?.[q.id];
      out.push({
        questionId: q.id,
        stem: q.stem || q.prompt || '',
        answer: typeof raw === 'string' ? raw : '',
      });
    }
  }
  return out;
}

/**
 * Build a full, section-grouped transcript of every answer a candidate gave —
 * the complete record a reviewer reads in the admin drill-down, beyond the
 * scored summary. Walks `instrument.groups` in order and normalizes each
 * question to a per-type shape so the renderer can switch cleanly.
 *
 * Like the other collectors, this is **read-only** — it derives nothing that
 * feeds `computeAxisScores` or `computeTechnicalScore`; it just pairs stored
 * answers with their question text and option labels for display.
 *
 * Per item (fields populated by `type`):
 *   - common:      questionId, type, stem, answered
 *   - likert-4 /
 *     forced-choice: chosenLabel, axisLabel, score   (the chosen option)
 *   - technical-mcq: chosenLabel, isCorrect, correctLabel, rationale
 *   - open-response: openText
 *
 * @param {object} instrument - parsed instrument JSON.
 * @param {Record<string, number|string>} responses - questionId -> answer.
 * @returns {Array<{ groupId: string, groupTitle: string, items: object[] }>}
 */
export function collectAllResponses(instrument, responses) {
  const axisLabelById = {};
  for (const axis of instrument.axes || []) axisLabelById[axis.id] = axis.label;

  return instrument.groups.map((group) => ({
    groupId: group.id,
    groupTitle: group.title || group.id,
    items: group.questions.map((q) => {
      const stem = q.stem || q.prompt || '';
      const raw = responses?.[q.id];

      if (q.type === 'open-response') {
        const text = typeof raw === 'string' ? raw : '';
        return { questionId: q.id, type: q.type, stem, answered: text !== '', openText: text };
      }

      const choiceIdx = typeof raw === 'number' ? raw : null;
      const chosen = choiceIdx != null ? q.options?.[choiceIdx] : null;
      const base = {
        questionId: q.id,
        type: q.type,
        stem,
        answered: chosen != null,
        chosenLabel: chosen?.label || '',
      };

      if (q.type === 'technical-mcq') {
        const correct = (q.options || []).find((o) => o.correct === true);
        const rationaleRaw = q.rationale ? responses?.[rationaleKey(q.id)] : undefined;
        return {
          ...base,
          isCorrect: chosen?.correct === true,
          correctLabel: correct?.label || '',
          rationale: q.rationale ? (typeof rationaleRaw === 'string' ? rationaleRaw : '') : null,
        };
      }

      // likert-4 / forced-choice (disposition): surface the chosen option's
      // axis contribution so the reviewer can see how it fed the quadrant.
      return {
        ...base,
        axisLabel: chosen ? axisLabelById[chosen.axis] || chosen.axis || '' : '',
        score: chosen ? chosen.score : null,
      };
    }),
  }));
}

/**
 * Map a technical percent (0–100) to a coarse band:
 *   strong (>= 75), mixed (50–74), weak (< 50).
 * Used to color-code the score in admin and candidate views — keeps the
 * thresholds in one place so UI and email digest stay consistent.
 */
export function techBand(percent) {
  if (percent >= 75) return 'strong';
  if (percent >= 50) return 'mixed';
  return 'weak';
}

/**
 * Pick the matching outcome based on normalized axis scores.
 *
 * Five-way result: four named quadrants plus `instrument.indeterminate` when
 * either axis score is within ±indeterminateThreshold of zero. The
 * Indeterminate outcome is a first-class result — distinct label, copy, and
 * downstream pill — not a footnote. A candidate whose signal is too weak to
 * place confidently lands here.
 *
 * Returns:
 *   { quadrant, indeterminate, xScore, yScore }
 *
 * `indeterminate` is a boolean for downstream renderers that want to branch.
 * `quadrant` is either the matched quadrant object, the
 * `instrument.indeterminate` block, or — for legacy instruments missing
 * `instrument.indeterminate` — the matched quadrant with no Indeterminate
 * handling (backward-compatible with v1/v2 submissions).
 */
export function resolveQuadrant(instrument, axisScores) {
  const [axisX, axisY] = instrument.axes;
  const xScore = axisScores[axisX.id];
  const yScore = axisScores[axisY.id];

  const threshold = instrument.indeterminateThreshold ?? DEFAULT_INDETERMINATE_THRESHOLD;
  const isIndeterminate =
    instrument.indeterminate &&
    (Math.abs(xScore) < threshold || Math.abs(yScore) < threshold);

  if (isIndeterminate) {
    return { quadrant: instrument.indeterminate, indeterminate: true, xScore, yScore };
  }

  const xDir = xScore >= 0 ? 'high' : 'low';
  const yDir = yScore >= 0 ? 'high' : 'low';
  const quadrant = instrument.quadrants.find(
    (q) => q.x === xDir && q.y === yDir
  );

  return { quadrant, indeterminate: false, xScore, yScore };
}

/**
 * Count total questions in the instrument.
 */
export function totalQuestions(instrument) {
  return instrument.groups.reduce((acc, g) => acc + g.questions.length, 0);
}

/**
 * Flatten ordered list of {groupIdx, question}.
 */
export function flattenQuestions(instrument) {
  const out = [];
  instrument.groups.forEach((g, groupIdx) => {
    g.questions.forEach((q) => {
      out.push({ groupIdx, group: g, question: q });
    });
  });
  return out;
}
